import { reactive, computed, ref, ComputedRef } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { useStorage } from '@vueuse/core'
import { DocumentFileImage } from '@/types/partials'
import { Collection } from '@/types'
import { useApi } from './useApi'
import { useFiles } from './useFiles'

export type DeliveryReactionStatus = {
    [key: string]: string | number
    id: number
    name: string
    symbol: string
}

export type AdviceDeliveryReaction = {
    id: number
    adviceId: number
    comment: string
    createdAt: string
    updatedAt: string
    deliveryReactionStatus: DeliveryReactionStatus
    deliveryReactionStatusId?: number
    images: DocumentFileImage[]
}

export type AdviceDeliveryReactionNewDocument = Pick<
    AdviceDeliveryReaction,
    'adviceId' | 'comment' | 'deliveryReactionStatusId'
> & {
    images: Pick<DocumentFileImage, 'fileName' | 'documentTypeId' | 'base64' | 'type'>[]
}

const adviceDeliveryReactions = reactive<Collection<AdviceDeliveryReaction>>(
    {} as Collection<AdviceDeliveryReaction>,
)
const loading = ref<boolean>(false)
const isEmpty = computed(() => adviceDeliveryReactions.items.length === 0)

const deliveryReactionNewDocumentsStorage = useStorage(
    'deliveryReactionNewDocuments',
    {},
    sessionStorage,
)

export const useAdviceDeliveryReactions = (
    adviceId?: number,
): {
    fetchAdviceDeliveryReactions: (adviceId: number, merge?: boolean) => Promise<void>
    downloadDocumentsZip: (adviceId: number) => Promise<void>
    addDeliveryReactionDocument: (newDocument: AdviceDeliveryReactionNewDocument) => Promise<void>
    adviceDeliveryReactions: ComputedRef<Collection<AdviceDeliveryReaction>>
    loading: ComputedRef<boolean>
    isEmpty: ComputedRef<boolean>
    updateDeliveryReactionNewDocumentsStorage: (
        newDocument: AdviceDeliveryReactionNewDocument,
    ) => void
    deleteFromDeliveryReactionNewDocumentsStorage: () => void
    newDocument: ComputedRef<AdviceDeliveryReactionNewDocument>
    clearAdviceDeliveryReactionsData: () => void
} => {
    const { useGet, usePost } = useApi()
    const fetchAdviceDeliveryReactions = async (adviceId: number, merge = false) => {
        loading.value = true
        clearAdviceDeliveryReactionsData()
        try {
            const { status, data } = await useGet(`/advices/delivery_reactions/${adviceId}`)
            if (status === 200 && data.data) {
                if (adviceDeliveryReactions.items === undefined) {
                    adviceDeliveryReactions.items = []
                }
                adviceDeliveryReactions.items = merge
                    ? adviceDeliveryReactions.items.concat(data.data)
                    : data.data
            }
        } finally {
            loading.value = false
        }
    }

    const downloadDocumentsZip = async (adviceId: number) => {
        const { getFile } = useFiles()
        try {
            await getFile(`/advices/delivery_reactions_zip/${adviceId}`, 'zip', uuidv4())
        } finally {
            loading.value = false
        }
    }

    const addDeliveryReactionDocument = async (newDocument: AdviceDeliveryReactionNewDocument) => {
        loading.value = true
        try {
            await usePost('/advices/delivery_reactions', newDocument)
        } finally {
            loading.value = false
        }
    }

    const _adviceId_ = ref<number | undefined>(adviceId)

    const updateDeliveryReactionNewDocumentsStorage = (
        newDocument: AdviceDeliveryReactionNewDocument,
    ): void => {
        if (_adviceId_.value) {
            Object.assign(deliveryReactionNewDocumentsStorage.value, {
                [_adviceId_.value]: newDocument,
            })
        }
    }

    const deleteFromDeliveryReactionNewDocumentsStorage = () => {
        if (_adviceId_.value) {
            Object.assign(deliveryReactionNewDocumentsStorage.value, {
                [_adviceId_.value]: {},
            })
        }
    }

    const clearAdviceDeliveryReactionsData = () => {
        adviceDeliveryReactions.items = []
    }

    return {
        fetchAdviceDeliveryReactions,
        downloadDocumentsZip,
        addDeliveryReactionDocument,
        adviceDeliveryReactions: computed(() => adviceDeliveryReactions),
        loading: computed(() => loading.value),
        isEmpty,
        updateDeliveryReactionNewDocumentsStorage,
        deleteFromDeliveryReactionNewDocumentsStorage,
        newDocument: computed(() => {
            if (_adviceId_.value) {
                return (deliveryReactionNewDocumentsStorage.value as Record<string, unknown>)[
                    _adviceId_.value
                ] as AdviceDeliveryReactionNewDocument
            }
            return {} as AdviceDeliveryReactionNewDocument
        }),
        clearAdviceDeliveryReactionsData,
    }
}
